import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoTip } from "@/components/ui/tooltip";
import {
  axisLegendHint,
  chartRightMargin,
  formatAxisMoney,
  formatAxisPercent,
  inferChartAxes,
  normalizeDataToPercent,
  rawKey,
  type ChartSeriesInput,
} from "@/lib/chartSeriesAxes";
import { cn, formatPLN } from "@/lib/utils";

export type HourlyPoint = {
  hour?: number;
  label: string;
  [key: string]: string | number | undefined;
};

function formatValue(v: number, format: "number" | "pln" = "number"): string {
  if (format === "pln") return formatPLN(v);
  return String(Math.round(v * 10) / 10);
}

function StackedAreaTooltip({
  active,
  label,
  payload,
}: {
  active?: boolean;
  label?: string;
  payload?: Array<{ name?: string; value?: number; color?: string; dataKey?: string }>;
}) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.dataKey !== "label");
  const sum = rows.reduce((acc, p) => acc + (Number(p.value) || 0), 0);
  return (
    <div
      className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-lg"
      style={{ background: "hsl(var(--card))" }}
    >
      <div className="mb-1.5 font-medium">Godzina {label}</div>
      {rows.map((p) => (
        <div key={String(p.dataKey || p.name)} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="tabular-nums">{formatValue(Number(p.value) || 0)}</span>
        </div>
      ))}
      <div className="mt-1.5 flex items-center justify-between border-t border-border/70 pt-1.5 font-medium">
        <span>Suma</span>
        <span className="tabular-nums">{formatValue(sum)}</span>
      </div>
    </div>
  );
}

export default function HourlyTrendChart({
  title,
  subtitle,
  data,
  series,
  mode = "line",
  scale = "absolute",
  emptyNote = "Brak danych godzinowych.",
  className,
  xInterval = 2,
}: {
  title: string;
  subtitle?: string;
  data: HourlyPoint[];
  series: ChartSeriesInput[];
  mode?: "bar" | "line" | "area";
  scale?: "absolute" | "percent";
  emptyNote?: string;
  className?: string;
  xInterval?: number;
}) {
  const percent = scale === "percent";
  const resolved = inferChartAxes(series, data);
  const hasData = data.some((row) =>
    resolved.some((s) => Number(row[s.key] ?? 0) > 0),
  );

  if (!hasData) {
    return (
      <Card className={cn(className)}>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-1.5">
            <CardTitle className="text-base">{title}</CardTitle>
            <InfoTip tipId="hourly_chart" />
          </div>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyNote}</p>
        </CardContent>
      </Card>
    );
  }

  const { data: chartData, resolved: drawn } = (() => {
    if (!percent) {
      return { data, resolved };
    }
    const { data: normalized } = normalizeDataToPercent(data, series);
    return {
      data: normalized,
      resolved: resolved.map((s) => ({ ...s, axisId: "left" as const })),
    };
  })();
  const axisHint = percent
    ? "Oś: 0-100% własnego maksimum każdej serii. Tooltip: liczby rzeczywiste."
    : axisLegendHint(drawn);
  const combinedSubtitle = [subtitle, axisHint].filter(Boolean).join(" · ");
  const hasRight = !percent && drawn.some((s) => s.axisId === "right");
  const hasMoney = !percent && drawn.some((s) => s.axisId === "rightMoney");
  const Chart = mode === "area" ? AreaChart : mode === "line" ? LineChart : BarChart;
  const stackedArea = mode === "area" && series.some((s) => Boolean(s.stackId));

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-1.5">
          <CardTitle className="text-base">{title}</CardTitle>
          <InfoTip tipId="hourly_chart" />
        </div>
        {combinedSubtitle && (
          <p className="text-xs text-muted-foreground">{combinedSubtitle}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="h-[240px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <Chart
              data={chartData}
              margin={{ top: 8, right: chartRightMargin(drawn), left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="label"
                stroke="hsl(var(--muted-foreground))"
                fontSize={10}
                interval={xInterval}
              />
              <YAxis
                yAxisId="left"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                allowDecimals={false}
                domain={percent ? [0, 100] : stackedArea ? [0, "auto"] : undefined}
                tickFormatter={percent ? formatAxisPercent : undefined}
                width={percent ? 36 : undefined}
              />
              {hasRight && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  allowDecimals={false}
                  width={32}
                />
              )}
              {hasMoney && (
                <YAxis
                  yAxisId="rightMoney"
                  orientation="right"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={10}
                  tickFormatter={formatAxisMoney}
                  width={44}
                  offset={hasRight ? 36 : 0}
                />
              )}
              {stackedArea ? (
                <Tooltip content={<StackedAreaTooltip />} />
              ) : (
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value: number, name: string, item) => {
                    const spec = drawn.find((s) => s.label === name || s.key === name);
                    const payload = item?.payload as Record<string, unknown> | undefined;
                    const raw = spec
                      ? Number(payload?.[rawKey(spec.key)] ?? value)
                      : Number(value) || 0;
                    return [
                      formatValue(raw, spec?.format || "number"),
                      spec?.label || name,
                    ];
                  }}
                  labelFormatter={(label) => `Godzina ${label}`}
                />
              )}
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {drawn.map((s) =>
                mode === "area" ? (
                  <Area
                    key={s.key}
                    type="stepAfter"
                    dataKey={s.key}
                    name={s.label}
                    yAxisId={s.axisId}
                    stackId={s.stackId || "area"}
                    stroke={s.color || "hsl(var(--brand))"}
                    fill={s.color || "hsl(var(--brand))"}
                    fillOpacity={0.58}
                    strokeWidth={1.5}
                  />
                ) : mode === "line" ? (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    yAxisId={s.axisId}
                    stroke={s.color || "hsl(var(--brand))"}
                    strokeWidth={2}
                    dot={false}
                  />
                ) : (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label}
                    yAxisId={s.axisId}
                    fill={s.color || "hsl(var(--brand))"}
                    stackId={s.stackId}
                    radius={s.stackId ? [0, 0, 0, 0] : [3, 3, 0, 0]}
                    maxBarSize={24}
                  />
                ),
              )}
            </Chart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
